/**
 * This has been tested for safety against ReDoS.
 *
 * Whenever updating it, please re-verify at
 * https://devina.io/redos-checker
 */
export const FILTER_EVENT_JSON =
  /(webkit-masked-url|chrome-extension|moz-extension|TypeError: Load failed|TypeError: Failed to fetch|TypeError: network error|NotFoundError: The object can not be found here|NotFoundError: Failed to execute 'removeChild'|AbortError: The user aborted a request|AbortError: Fetch is aborted|NetworkError when attempting to fetch resource|TypeError: cancelled|does not match URL at time of hydration|\.aspx|\/\.env| RS SDK |\/auraFW\/|liveagent\.esw|esw\.min\.js|analytics-js-common|"Not Found","internal":true|\/meta\.json)/i;

const ALLOWED_REQUEST_HEADERS = new Set([
  'accept',
  'content-type',
  'host',
  'referer',
  'user-agent',
]);

const REDACTED_QUERY_PARAMS = new Set([
  'code',
  'email',
  'invite',
  'search',
  'state',
  'uid',
]);

const REDACTED_QUERY_PARAM_PATTERN = /token|password|secret/i;

const QUERY_PARAM = /(^|[?&])([^=&#?/\s"'\\]+)=[^&#\s"'\\]*/g;

const BEARER_LINK_PATHS = [
  /(\/(?:coapplicant|guarantor)\/[^/?#\s"'\\]+\/)[^/?#\s"'\\]+/g,
  /(\/)[\da-f](?:-?[\da-f]){31}(?=\/pets\b)/gi,
];

function isRedactedQueryParam(key: string): boolean {
  return (
    REDACTED_QUERY_PARAMS.has(key.toLowerCase()) ||
    REDACTED_QUERY_PARAM_PATTERN.test(key)
  );
}

function redactText(text: string): string {
  return BEARER_LINK_PATHS.reduce(
    (redacted, pattern) => redacted.replace(pattern, '$1[Filtered]'),
    text
  ).replace(QUERY_PARAM, (match, separator: string, key: string) =>
    isRedactedQueryParam(key) ? `${separator}${key}=[Filtered]` : match
  );
}

type Fields = Record<string, unknown>;

function redactFields(fields: Fields | undefined): void {
  if (!fields) return;

  for (const [key, value] of Object.entries(fields)) {
    if (typeof value === 'string') fields[key] = redactText(value);
  }
}

export type ScrubbableEvent = {
  message?: string;
  transaction?: string;
  exception?: { values?: { value?: string }[] };
  request?: {
    url?: string;
    headers?: Record<string, string>;
    cookies?: unknown;
    query_string?: unknown;
    data?: unknown;
  };
  breadcrumbs?: { message?: string; data?: Fields }[];
  spans?: { description?: string; data?: Fields }[];
  contexts?: { trace?: { data?: Fields } };
  tags?: Fields;
  extra?: Fields;
};

function scrubRequest(request: ScrubbableEvent['request']): void {
  if (!request) return;

  delete request.cookies;
  delete request.data;
  if (typeof request.query_string === 'string') {
    request.query_string = redactText(request.query_string);
  } else {
    delete request.query_string;
  }
  if (request.url) request.url = redactText(request.url);
  if (request.headers) {
    request.headers = Object.fromEntries(
      Object.entries(request.headers)
        .filter(([name]) => ALLOWED_REQUEST_HEADERS.has(name.toLowerCase()))
        .map(([name, value]) => [name, redactText(value)])
    );
  }
}

export function scrubSensitiveData<T extends ScrubbableEvent>(event: T): T {
  scrubRequest(event.request);
  if (event.message) event.message = redactText(event.message);
  if (event.transaction) event.transaction = redactText(event.transaction);
  event.exception?.values?.forEach((exception) => {
    if (exception.value) exception.value = redactText(exception.value);
  });
  event.breadcrumbs?.forEach((breadcrumb) => {
    if (breadcrumb.message) breadcrumb.message = redactText(breadcrumb.message);
    redactFields(breadcrumb.data);
  });
  event.spans?.forEach((span) => {
    if (span.description) span.description = redactText(span.description);
    redactFields(span.data);
  });
  redactFields(event.contexts?.trace?.data);
  redactFields(event.tags);
  redactFields(event.extra);

  return event;
}
